import React, { type ComponentType, lazy, type LazyExoticComponent, Suspense } from "react";

import { cn, RedesignAppShell, type RedesignAppShellProps, RedesignRoot } from "~/common/redesign-v1";

type ShellConfig = Pick<RedesignAppShellProps, "activeNav" | "breadcrumbRoot" | "breadcrumbTrail" | "breadcrumbLeaf" | "user" | "manager" | "fullHeight" | "plainSurface" | "tightFooter" | "footer">;

/** Lightweight in-shell placeholder while a page chunk loads — shell + nav stay mounted. */
export function RedesignContentFallback({ testId }: { testId: string }) {
  return <div data-testid={testId} aria-busy="true" className="tw-flex tw-min-h-[40vh] tw-w-full tw-items-center tw-justify-center" />;
}

/**
 * Standard redesign route entry: eager shell, lazy page body.
 * Keeps sidebar/bottom nav visible during chunk download (avoids the full-viewport blank flash).
 * `shell` may be a static config or a function of the route props (dynamic breadcrumbs with patient id).
 */
export function createRedesignEntry<P extends object>(
  importPage: () => Promise<{ default: ComponentType<P> }>,
  { testId, shell }: { testId: string; shell?: ShellConfig | ((props: P) => ShellConfig) },
): ComponentType<P> {
  const LazyPage: LazyExoticComponent<ComponentType<P>> = lazy(importPage);
  // TypeScript cannot verify that an unresolved generic `P` satisfies JSX's attribute constraints, so
  // spreading it directly errors even though every call site is sound. Narrow the cast to exactly that
  // spread rather than loosening the exported signature — `createRedesignEntry` stays `ComponentType<P>`,
  // so call sites keep full prop checking.
  const Page = LazyPage as unknown as ComponentType<Record<string, unknown>>;

  function RedesignEntry(props: P) {
    const resolvedShell = typeof shell === "function" ? shell(props) : shell;
    return (
      <RedesignRoot>
        <RedesignAppShell {...resolvedShell}>
          <Suspense fallback={<RedesignContentFallback testId={testId} />}>
            <Page {...(props as Record<string, unknown>)} />
          </Suspense>
        </RedesignAppShell>
      </RedesignRoot>
    );
  }

  return RedesignEntry;
}

/**
 * Legacy bodies share this card wrapper inside the shell content area.
 *
 * `surface` (default true) draws the WHITE `bg-background` card. Set it FALSE for a page that owns its own
 * layout on the grey shell — e.g. chat, whose title sits
 * on the grey and its chat panels are a capped white surface, so a white card here would put the title on
 * white (a figma regression). With `surface={false}` the wrapper is transparent (the shell `bg-canvas` shows).
 */
export function RedesignLegacyCard({ activeNav, surface = true, fill = false, children }: { activeNav: string; surface?: boolean; fill?: boolean; children: React.ReactNode }) {
  // `fill` (chat / any fullHeight page): the wrapper must be a flex-fill COLUMN so the shell's bounded
  // viewport height propagates down to the child's `flex-1` scroll region. A plain block here (the default)
  // grows to content height instead, which pushes a bottom-pinned composer off-screen (the chat regression).
  return (
    <div
      data-testid={`rd-${activeNav}-page`}
      className={cn(surface ? "tw-min-w-0 tw-rounded-xl tw-bg-background tw-p-3 tw-shadow-sm desktop:tw-p-5" : "tw-min-w-0", fill && "tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col")}
    >
      {children}
    </div>
  );
}
